import { createRng } from "../rng";
import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
} from "../types";

/**
 * Race three pegs around a shared loop. Landing on somebody sends them home,
 * which is the whole game: the die decides how far you go, but *which peg* you
 * move is entirely yours, and that is where it is won.
 *
 * Deliberately not called Trouble — the name and its plastic dome belong to
 * somebody, and the game underneath is centuries older than either.
 */

export const PEG_TRACK = 24;
export const PEG_COUNT = 3;
/** Roll this to bring a peg out of the nest, and roll again. */
export const PEG_START_ROLL = 6;
/** Turns before the race is called on who is furthest round. */
export const PEG_MOVE_LIMIT = 120;

/** Where each player joins the loop. Opposite sides, so there is a run home. */
const ENTRY: [number, number] = [0, PEG_TRACK / 2];

/**
 * A peg is how far round its own journey it is: -1 waiting in the nest, 0 to
 * TRACK-1 out on the loop, and TRACK once it is safely home. Counting from each
 * player's own entry means "am I past the end" is the same sum for both.
 */
export type Peg = number;

export interface PegChaseState extends BaseState {
	kind: "pegchase";
	pegs: [Peg[], Peg[]];
	/** The roll waiting to be used, or null when it needs rolling. */
	roll: number | null;
	/**
	 * Every roll of the game, derived rather than stored.
	 *
	 * `apply` takes no generator — the whole architecture is that a move is a
	 * pure function of the state — so a die has to come from somewhere already
	 * in the state. Keeping a seed and a count means the sequence is fixed at
	 * the start and unrolled one at a time, which is deterministic for the tests
	 * and, because the seed is redacted, unreadable to a player who would
	 * otherwise know every roll of the game before it started.
	 */
	rollSeed: number;
	rollsUsed: number;
	/** Set after a six, which earns another go. */
	rollAgain: boolean;
	movesMade: number;
	lastNote: string;
}

export type PegChaseMove = { roll: true } | { peg: number };

/** The nth roll of a game seeded with `seed`. Pure, so a test can replay it. */
function rollAt(seed: number, index: number): number {
	return 1 + createRng((seed + index * 7919) >>> 0).int(6);
}

/** The square on the shared loop a peg is standing on, or null if it is not. */
export function squareOf(player: PlayerIndex, peg: Peg): number | null {
	if (peg < 0 || peg >= PEG_TRACK) return null;
	return (ENTRY[player] + peg) % PEG_TRACK;
}

/** Where `peg` would land, or null when the roll does not fit. */
function landing(peg: Peg, roll: number): Peg | null {
	if (peg < 0) return roll === PEG_START_ROLL ? 0 : null;
	const next = peg + roll;
	// Overshooting home wastes the roll rather than wrapping round again.
	return next <= PEG_TRACK ? next : null;
}

function movablePegs(state: PegChaseState, player: PlayerIndex): number[] {
	if (state.roll === null) return [];
	const roll = state.roll;
	const mine = state.pegs[player];

	return mine
		.map((peg, index) => ({ peg, index }))
		.filter(({ peg }) => landing(peg, roll) !== null)
		.filter(({ peg, index }) => {
			// You cannot land on your own peg, unless it is already home.
			const target = landing(peg, roll) as number;
			if (target >= PEG_TRACK) return true;
			return !mine.some((other, i) => i !== index && other === target);
		})
		.map(({ index }) => index);
}

export const pegChase: GameDefinition<PegChaseState, PegChaseMove> = {
	id: "pegchase",
	category: "classics",
	title: "Peg Chase",
	emoji: "🎲",
	blurb: "Race your pegs round the board. Land on your friend and they go home!",
	howTo: [
		"Roll the die, then choose which of your pegs to move.",
		"Roll a six to bring a peg out, and you get another go.",
		"Land on your friend's peg and it goes all the way back to the start.",
	],

	create(rng) {
		return {
			kind: "pegchase",
			pegs: [Array(PEG_COUNT).fill(-1), Array(PEG_COUNT).fill(-1)],
			roll: null,
			rollSeed: rng.int(0x7fffffff),
			rollsUsed: 0,
			rollAgain: false,
			movesMade: 0,
			lastNote: "Roll to start!",
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const value = raw as { roll?: unknown; peg?: unknown };
		if (value.roll === true) return { roll: true };
		if (typeof value.peg === "number" && Number.isInteger(value.peg)) {
			return value.peg >= 0 && value.peg < PEG_COUNT ? { peg: value.peg } : null;
		}
		return null;
	},

	apply(state, player, move): MoveOutcome<PegChaseState> {
		if (state.result) return reject("This race is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		if ("roll" in move) {
			if (state.roll !== null) return reject("Move a peg first.");
			// The die is thrown on the server, so nobody can choose their own six.
			const roll = rollAt(state.rollSeed, state.rollsUsed);
			const rolled: PegChaseState = {
				...state,
				roll,
				rollsUsed: state.rollsUsed + 1,
				rollAgain: roll === PEG_START_ROLL,
				lastNote: `Rolled a ${roll}.`,
			};

			// A roll nobody can use passes the turn rather than stalling.
			if (movablePegs(rolled, player).length === 0) {
				return {
					ok: true,
					state: {
						...rolled,
						roll: null,
						rollAgain: false,
						turn: otherPlayer(player),
						movesMade: state.movesMade + 1,
						lastNote: `Rolled a ${roll} — nothing to move.`,
					},
				};
			}
			return { ok: true, state: rolled };
		}

		if (state.roll === null) return reject("Roll the die first.");
		if (!movablePegs(state, player).includes(move.peg)) {
			return reject("That peg can't make that move.");
		}

		const roll = state.roll;
		const pegs: [Peg[], Peg[]] = [[...state.pegs[0]], [...state.pegs[1]]];
		const target = landing(pegs[player][move.peg], roll) as number;
		pegs[player][move.peg] = target;

		// Landing on somebody sends them all the way back to the nest.
		let bumped = false;
		const square = squareOf(player, target);
		if (square !== null) {
			const them = otherPlayer(player);
			pegs[them] = pegs[them].map((peg) => {
				if (squareOf(them, peg) === square) {
					bumped = true;
					return -1;
				}
				return peg;
			});
		}

		const home: [number, number] = [
			pegs[0].filter((peg) => peg >= PEG_TRACK).length,
			pegs[1].filter((peg) => peg >= PEG_TRACK).length,
		];
		const movesMade = state.movesMade + 1;
		const next: PegChaseState = {
			...state,
			pegs,
			roll: null,
			rollAgain: false,
			movesMade,
			scores: home,
			lastNote: bumped ? "Sent one home!" : target >= PEG_TRACK ? "That one's home!" : "Moved.",
		};

		if (home[player] === PEG_COUNT) {
			return { ok: true, state: { ...next, turn: null, result: { winner: player } } };
		}
		if (movesMade >= PEG_MOVE_LIMIT) {
			const reach = (side: PlayerIndex) =>
				pegs[side].reduce((total, peg) => total + Math.max(0, peg), 0);
			const winner = reach(0) === reach(1) ? null : reach(0) > reach(1) ? 0 : 1;
			return { ok: true, state: { ...next, turn: null, result: { winner } } };
		}

		// A six earns another go, which is what makes them worth waiting for.
		return { ok: true, state: { ...next, turn: state.rollAgain ? player : otherPlayer(player) } };
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		if (state.roll === null) return [{ roll: true }];
		return movablePegs(state, player).map((peg) => ({ peg }));
	},

	/** The seed is every future roll at once, so it never leaves the server. */
	redact(state) {
		return { ...state, rollSeed: 0 };
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},
};

/**
 * Sending somebody home is worth more than anything else; after that, getting a
 * peg out and getting one home. Not a search — the die makes that pointless —
 * but the ordering is what a child works out after a game or two.
 */
export function bestPegMove(state: PegChaseState, player: PlayerIndex): PegChaseMove | null {
	const moves = pegChase.legalMoves(state, player);
	if (moves.length === 0) return null;
	if (moves.length === 1) return moves[0];

	let best = moves[0];
	let bestScore = Number.NEGATIVE_INFINITY;
	for (const move of moves) {
		if (!("peg" in move)) continue;
		const outcome = pegChase.apply(state, player, move, 0);
		if (!outcome.ok) continue;

		const before = state.pegs[otherPlayer(player)].filter((peg) => peg >= 0).length;
		const after = outcome.state.pegs[otherPlayer(player)].filter((peg) => peg >= 0).length;
		const target = outcome.state.pegs[player][move.peg];

		let score = (before - after) * 100;
		if (target >= PEG_TRACK) score += 50;
		if (state.pegs[player][move.peg] < 0) score += 20;
		score += target;

		if (score > bestScore) {
			bestScore = score;
			best = move;
		}
	}
	return best;
}
